/**
 * PermissionPicker - selector de permisos de un modulo: combo de entrada y tags.
 *
 * Cada permiso concedido es un tag, y se quita con su "x". El combo solo ofrece lo
 * que falta, asi que no hace falta un multiselect con casillas marcadas: se elige
 * una vez y el resultado se ve en la misma linea, que es la razon de usar tags.
 *
 * Las acciones que el backend concede siempre (perm_always_granted en PHP, published
 * en el catalogo como always_granted) se pintan ya puestas y sin "x", con candado.
 * No es una decision de esta pantalla: el servidor las vuelve a poner al guardar, asi
 * que ofrecer un "quitar" que luego no se respeta seria una mentira.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { granted, isForced, label, optionalActions } from './roleMatrix';
import { theme } from '../../../theme';

export function PermissionPicker({ mod, draft, disabled, onSetAction, onSetModule }) {
    const from = draft[mod.code] || {};
    const on = mod.actions.filter((a) => granted(from[a.code]));
    const missing = optionalActions(mod, draft);
    const selectable = mod.actions.filter((a) => !isForced(a));
    const allOn = selectable.length > 0 && selectable.every((a) => granted(from[a.code]));

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <select
                    className="form-select form-select-sm bg-dark text-white border-secondary"
                    style={{ width: 'auto', minWidth: '13rem' }}
                    value=""
                    disabled={disabled || missing.length === 0}
                    aria-label={`Agregar permiso a ${label(mod)}`}
                    onChange={(e) => {
                        const code = e.target.value;
                        if (code) onSetAction(mod.code, code, true);
                    }}
                >
                    <option value="">
                        {missing.length === 0 ? 'Sin permisos por agregar' : 'Agregar permiso...'}
                    </option>
                    {missing.map((a) => (
                        <option key={a.code} value={a.code}>{label(a)}</option>
                    ))}
                </select>

                {selectable.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary border-secondary text-white-50 px-3"
                        onClick={() => onSetModule(mod.code, selectable.map((a) => a.code), !allOn)}
                        disabled={disabled}
                    >
                        {allOn ? 'Quitar todas' : 'Agregar todas'}
                    </button>
                )}
            </div>

            {on.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                    {on.map((a) => {
                        const forced = isForced(a);
                        return (
                            <span
                                key={a.code}
                                className="badge d-inline-flex align-items-center gap-2 py-2 px-2 rounded-2"
                                style={{
                                    background: forced ? theme.successSoft : `rgba(\${theme.primaryRgb}, 0.18)`,
                                    border: forced ? '1px solid rgba(25,135,84,0.55)' : `1px solid rgba(${theme.primaryRgb}, 0.35)`,
                                    color: 'var(--ink-text)',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                }}
                            >
                                {label(a)}
                                {forced ? (
                                    <FontAwesomeIcon
                                        icon={faLock}
                                        title="Este permiso se concede siempre a cualquier rol"
                                        style={{ fontSize: '0.7rem', opacity: 0.85 }}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-sm p-0 border-0 text-white-50"
                                        style={{ lineHeight: 1 }}
                                        disabled={disabled}
                                        aria-label={`Quitar ${label(a)} de ${label(mod)}`}
                                        title="Quitar"
                                        onClick={() => onSetAction(mod.code, a.code, false)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </span>
                        );
                    })}
                </div>
            ) : (
                <span className="text-white-50" style={{ fontSize: '0.8rem' }}>
                    Este modulo no concede permisos a este rol.
                </span>
            )}
        </div>
    );
}

export default PermissionPicker;
